import React, { useState } from 'react';
import { useTranslation } from '../../../../shared/i18n';
import { useReferralData } from '../../../referral/hooks/useReferralData';
import StatsCard from '../../../referral/components/StatsCard';
import ReferredUsersList from '../../../referral/components/ReferredUsersList';
import ReferralLinkCard from '../../../referral/components/ReferralLinkCard';
import RecentEarnings from '../../../referral/components/RecentEarnings';
import PayoutInfo from '../../../referral/components/PayoutInfo';
import CashbackSettings from '../../../referral/components/CashbackSettings';

/**
 * Referrals - Referral program page component for guardian dashboard
 * Reuses components from the referral app
 */
export const Referrals = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [localCashbackSettings, setLocalCashbackSettings] = useState(null);
  const { data, loading, error } = useReferralData(currentPage, 5);
  const { t: i18nT } = useTranslation();

  const railsEnv = window.railsEnv || 'production';
  const isTestMode = railsEnv === 'test' || railsEnv === 'development';

  if (loading) {
    return (
      <div className="bg-light">
        <div className="container py-4">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{i18nT('common.loading')}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-light">
        <div className="container py-4">
          <div className="alert alert-danger" role="alert">
            {i18nT('common.error')}: {error}
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const { stats, referrals, pagination, recent_earnings, referral_link, payout_info, cashback_settings, translations } = data;
  const t = translations || {};

  // Use local state if available (after save), otherwise use data from API
  const currentCashbackSettings = localCashbackSettings || cashback_settings;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN').format(amount);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCashbackSave = (newSettings) => {
    setLocalCashbackSettings(newSettings);
  };

  return (
    <div className="bg-light">
      <div className="container py-4">
        {/* Page Header */}
        <div className="mb-4">
          <h1 className="display-6 fw-bold mb-2">{t.header?.title}</h1>
          <p className="text-muted">{t.header?.description}</p>
        </div>

        {/* Stats Cards Row */}
        <div className="row g-3 mb-4">
          <StatsCard
            icon="bi-people-fill"
            iconBg="#dbeafe"
            iconColor="#3b82f6"
            badge={t.stats?.total_badge}
            badgeClass="badge bg-secondary bg-opacity-10 text-secondary"
            value={stats.total_referrals}
            label={t.stats?.referred_users}
            testId="total-referrals"
          />
          <StatsCard
            icon="bi-check-circle-fill"
            iconBg="#dcfce7"
            iconColor="#16a34a"
            badge={t.stats?.active_badge}
            badgeClass="badge bg-success bg-opacity-10 text-success"
            value={stats.active_referrals}
            label={t.stats?.active_referrals}
            testId="active-referrals"
          />
          <StatsCard
            icon="bi-currency-dollar"
            iconBg="#f3e8ff"
            iconColor="#9333ea"
            value={`${formatCurrency(stats.total_earnings)} ₫`}
            label={t.stats?.total_earnings}
            testId="total-earnings"
          />
          <StatsCard
            icon="bi-clock-fill"
            iconBg="#fed7aa"
            iconColor="#ea580c"
            badge={t.stats?.available_badge}
            badgeClass="badge bg-warning bg-opacity-10 text-warning"
            value={`${formatCurrency(stats.pending_earnings)} ₫`}
            label={t.stats?.pending_payout}
            testId="pending-earnings"
          />
        </div>

        <div className="row g-4">
          {/* Main Column - Referred Users Table */}
          <ReferredUsersList
            referrals={referrals}
            pagination={pagination}
            onPageChange={handlePageChange}
            translations={translations}
            railsEnv={railsEnv}
          />

          {/* Sidebar Column */}
          <div className="col-lg-4">
            <ReferralLinkCard
              referralLink={referral_link}
              translations={translations}
              railsEnv={railsEnv}
              cashbackSettings={currentCashbackSettings}
            />

            <div className="mt-4">
              <CashbackSettings
                cashbackSettings={currentCashbackSettings}
                translations={translations}
                onSave={handleCashbackSave}
              />
            </div>

            <RecentEarnings
              earnings={recent_earnings}
              translations={translations}
              railsEnv={railsEnv}
            />

            <PayoutInfo
              payoutInfo={payout_info}
              translations={translations}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Referrals;
